import type { ReactNode } from 'react';
import { useRef } from 'react';
import { ThemeSurface } from '@wix/da-react-context/pkg/ThemeContext';
import Surface from '@wix/da-ds/pkg/Surface';
import ScrollableContent from '@wix/da-ds/pkg/ScrollableContent';
import Loader from '@wix/da-ds/pkg/Loader';
import Pagination from '../../../StudioPagination';

import s from './ScrollableList.scss';

interface Props<ResultType> {
  beforeList?: ReactNode;
  results: ResultType[];
  renderItem: (
    result: ResultType,
    idx: number,
    containerRef
  ) => ReactNode;
  isFetching?: boolean;
  showPagination?: boolean;
}

function ScrollableList<ResultType>({
  beforeList,
  results,
  renderItem,
  isFetching,
  showPagination = true,
}: Props<ResultType>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollableRef = useRef<HTMLElement | null>(null);
  const scrollToTop = () => {
    scrollableRef.current && scrollableRef.current.scrollTo({ top: 0 });
  };
  return (
    <>
      <ScrollableContent innerRef={el => (scrollableRef.current = el)}>
        {beforeList}
        <Surface className={s['list-wrapper']} type={ThemeSurface.SECONDARY}>
          <div className={s['list']} ref={containerRef}>
            {results.map((result, idx) =>
              renderItem(result, idx, containerRef)
            )}
          </div>
        </Surface>
      </ScrollableContent>

      {isFetching && (
        <div className={s['loading']}>
          <Loader state="loading" size="big" />
        </div>
      )}

      {showPagination && !isFetching && (
        <Pagination className={s['pagination']} onFetchSuccess={scrollToTop} />
      )}
    </>
  );
}
ScrollableList.displayName = 'ScrollableList';

export default ScrollableList;
